import React, { useState, useEffect } from 'react';
import {
  Key,
  Search,
  Filter,
  Plus,
  RefreshCw,
  Mail,
  Edit,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  TrendingUp,
  Shield,
  Download
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';

const AdminLicenseManager = () => {
  const [licenses, setLicenses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const API_URL = 'https://cyberguard-backend-wa00.onrender.com';

  useEffect(() => {
    loadStats();
    loadLicenses();
  }, [currentPage, statusFilter, typeFilter]);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/licenses/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadLicenses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        skip: ((currentPage - 1) * 20).toString(),
        limit: '20'
      });

      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('license_type', typeFilter);

      const response = await fetch(`${API_URL}/api/admin/licenses?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLicenses(data.licenses);
        setTotalPages(data.pages);
      }
    } catch (err) {
      setError('Failed to load licenses');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadLicenses();
  };

  const handleCreateLicense = async (licenseData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/license/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(licenseData)
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(`License created: ${data.license.license_key}`);
        setShowCreateModal(false);
        loadLicenses();
        loadStats();
      } else {
        setError('Failed to create license');
      }
    } catch (err) {
      setError('Failed to create license');
    }
  };

  const handleExtendLicense = async (licenseKey, days) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/license/${licenseKey}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ duration_days: days })
      });

      if (response.ok) {
        setSuccess(`License extended by ${days} days`);
        loadLicenses();
      } else {
        setError('Failed to extend license');
      }
    } catch (err) {
      setError('Failed to extend license');
    }
  };

  const handleResendEmail = async (licenseKey, email) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/license/${licenseKey}/email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setSuccess(`License email sent to ${email}`);
      } else {
        setError('Failed to send email');
      }
    } catch (err) {
      setError('Failed to send email');
    }
  };

  const handleRevokeLicense = async (licenseKey) => {
    if (!window.confirm('Are you sure you want to revoke this license? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/license/${licenseKey}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('License revoked successfully');
        loadLicenses();
        loadStats();
      } else {
        setError('Failed to revoke license');
      }
    } catch (err) {
      setError('Failed to revoke license');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: { bg: '#10b981', text: 'Active' },
      expired: { bg: '#f59e0b', text: 'Expired' },
      suspended: { bg: '#ef4444', text: 'Suspended' },
      revoked: { bg: '#6b7280', text: 'Revoked' }
    };

    const style = styles[status] || styles.active;

    return (
      <Badge style={{
        background: `${style.bg}20`,
        color: style.bg,
        border: `1px solid ${style.bg}40`
      }}>
        {style.text}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const styles = {
      individual: { color: '#10b981', label: 'Individual' },
      professional: { color: '#3b82f6', label: 'Professional' },
      business: { color: '#8b5cf6', label: 'Business' },
      enterprise: { color: '#f59e0b', label: 'Enterprise' }
    };

    const style = styles[type] || styles.individual;

    return (
      <Badge style={{
        background: `${style.color}20`,
        color: style.color,
        border: `1px solid ${style.color}40`
      }}>
        {style.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#ffffff', marginBottom: '8px' }}>
          License Management
        </h1>
        <p style={{ color: '#94a3b8' }}>
          Manage all CyberGuard licenses, monitor usage, and handle customer support
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <Alert style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          marginBottom: '20px'
        }}>
          <AlertTriangle className="h-4 w-4" style={{ color: '#ef4444' }} />
          <AlertDescription style={{ color: '#ef4444' }}>{error}</AlertDescription>
          <Button
            size="sm"
            onClick={() => setError(null)}
            style={{ marginLeft: 'auto', background: 'transparent', border: 'none' }}
          >
            <XCircle className="h-4 w-4" />
          </Button>
        </Alert>
      )}

      {success && (
        <Alert style={{
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          marginBottom: '20px'
        }}>
          <CheckCircle className="h-4 w-4" style={{ color: '#10b981' }} />
          <AlertDescription style={{ color: '#10b981' }}>{success}</AlertDescription>
          <Button
            size="sm"
            onClick={() => setSuccess(null)}
            style={{ marginLeft: 'auto', background: 'transparent', border: 'none' }}
          >
            <XCircle className="h-4 w-4" />
          </Button>
        </Alert>
      )}

      {/* Stats Dashboard */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <Card style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(0, 212, 255, 0.2)'
          }}>
            <CardContent style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '4px' }}>Total Licenses</p>
                  <p style={{ color: '#ffffff', fontSize: '32px', fontWeight: 'bold' }}>{stats.total_licenses}</p>
                </div>
                <Key className="h-8 w-8" style={{ color: '#00d4ff' }} />
              </div>
            </CardContent>
          </Card>

          <Card style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(16, 185, 129, 0.2)'
          }}>
            <CardContent style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '4px' }}>Active</p>
                  <p style={{ color: '#ffffff', fontSize: '32px', fontWeight: 'bold' }}>{stats.active_licenses}</p>
                </div>
                <CheckCircle className="h-8 w-8" style={{ color: '#10b981' }} />
              </div>
            </CardContent>
          </Card>

          <Card style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(239, 68, 68, 0.2)'
          }}>
            <CardContent style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '4px' }}>Violations</p>
                  <p style={{ color: '#ffffff', fontSize: '32px', fontWeight: 'bold' }}>{stats.total_violations}</p>
                </div>
                <AlertTriangle className="h-8 w-8" style={{ color: '#ef4444' }} />
              </div>
            </CardContent>
          </Card>

          <Card style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(251, 191, 36, 0.2)'
          }}>
            <CardContent style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '4px' }}>Expiring Soon</p>
                  <p style={{ color: '#ffffff', fontSize: '32px', fontWeight: 'bold' }}>{stats.expiring_soon}</p>
                </div>
                <Clock className="h-8 w-8" style={{ color: '#fbbf24' }} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <Card style={{
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        marginBottom: '20px'
      }}>
        <CardContent style={{ padding: '20px' }}>
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ flex: '1', minWidth: '250px' }}>
              <Input
                placeholder="Search by email, license key, or customer..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  color: '#ffffff'
                }}
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                color: '#ffffff',
                padding: '8px 12px',
                borderRadius: '6px'
              }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="suspended">Suspended</option>
              <option value="revoked">Revoked</option>
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                color: '#ffffff',
                padding: '8px 12px',
                borderRadius: '6px'
              }}
            >
              <option value="">All Types</option>
              <option value="individual">Individual</option>
              <option value="professional">Professional</option>
              <option value="business">Business</option>
              <option value="enterprise">Enterprise</option>
            </select>

            <Button
              onClick={handleSearch}
              style={{
                background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
                color: '#ffffff'
              }}
            >
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>

            <Button
              onClick={() => setShowCreateModal(true)}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#ffffff'
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create License
            </Button>

            <Button
              onClick={() => { loadLicenses(); loadStats(); }}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                border: '1px solid rgba(0, 212, 255, 0.3)'
              }}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* License List */}
      <Card style={{
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(0, 212, 255, 0.2)'
      }}>
        <CardHeader>
          <CardTitle style={{ color: '#ffffff' }}>
            Licenses ({licenses.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
              Loading licenses...
            </div>
          ) : licenses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
              No licenses found
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(0, 212, 255, 0.2)' }}>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '14px' }}>License Key</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '14px' }}>Type</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '14px' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '14px' }}>Customer</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '14px' }}>Devices</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '14px' }}>Expires</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '14px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.map((license, index) => (
                    <tr
                      key={index}
                      style={{
                        borderBottom: '1px solid rgba(0, 212, 255, 0.1)',
                        background: index % 2 === 0 ? 'transparent' : 'rgba(0, 212, 255, 0.02)'
                      }}
                    >
                      <td style={{ padding: '12px' }}>
                        <code style={{ color: '#00d4ff', fontSize: '13px', fontFamily: 'monospace' }}>
                          {license.license_key}
                        </code>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {getTypeBadge(license.license_type)}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {getStatusBadge(license.status)}
                      </td>
                      <td style={{ padding: '12px', color: '#e2e8f0', fontSize: '14px' }}>
                        {license.contact_email || 'N/A'}
                      </td>
                      <td style={{ padding: '12px', color: '#e2e8f0', fontSize: '14px' }}>
                        {license.active_devices} / {license.max_devices}
                      </td>
                      <td style={{ padding: '12px', color: '#e2e8f0', fontSize: '14px' }}>
                        {formatDate(license.expires_at)}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button
                            size="sm"
                            onClick={() => handleExtendLicense(license.license_key, 30)}
                            title="Extend 30 days"
                            style={{
                              background: 'rgba(16, 185, 129, 0.1)',
                              border: '1px solid rgba(16, 185, 129, 0.3)',
                              color: '#10b981',
                              padding: '6px 10px'
                            }}
                          >
                            +30d
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleResendEmail(license.license_key, license.contact_email)}
                            title="Resend email"
                            style={{
                              background: 'rgba(59, 130, 246, 0.1)',
                              border: '1px solid rgba(59, 130, 246, 0.3)',
                              color: '#3b82f6',
                              padding: '6px'
                            }}
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleRevokeLicense(license.license_key)}
                            title="Revoke license"
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              color: '#ef4444',
                              padding: '6px'
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              marginTop: '20px',
              paddingTop: '20px',
              borderTop: '1px solid rgba(0, 212, 255, 0.2)'
            }}>
              <Button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  color: '#ffffff',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                Previous
              </Button>
              <span style={{ color: '#94a3b8' }}>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  color: '#ffffff',
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create License Modal - Placeholder for now */}
      {showCreateModal && (
        <CreateLicenseModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateLicense}
        />
      )}
    </div>
  );
};

// Create License Modal Component
const CreateLicenseModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    license_type: 'professional',
    max_devices: 3,
    duration_days: 365,
    contact_email: '',
    company_name: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <Card style={{
        background: 'rgba(15, 20, 30, 0.98)',
        border: '2px solid rgba(0, 212, 255, 0.3)',
        borderRadius: '1.5rem',
        maxWidth: '500px',
        width: '100%',
        margin: '20px'
      }}>
        <CardHeader>
          <CardTitle style={{ color: '#ffffff' }}>Create New License</CardTitle>
          <CardDescription style={{ color: '#94a3b8' }}>
            Generate a new license key for a customer
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div>
              <label style={{ color: '#e2e8f0', fontSize: '14px', marginBottom: '6px', display: 'block' }}>
                Customer Email *
              </label>
              <Input
                type="email"
                required
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  color: '#ffffff'
                }}
              />
            </div>

            <div>
              <label style={{ color: '#e2e8f0', fontSize: '14px', marginBottom: '6px', display: 'block' }}>
                License Type *
              </label>
              <select
                value={formData.license_type}
                onChange={(e) => {
                  const type = e.target.value;
                  const devices = type === 'individual' ? 1 : type === 'professional' ? 3 : type === 'business' ? 10 : 999;
                  setFormData({ ...formData, license_type: type, max_devices: devices });
                }}
                style={{
                  width: '100%',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  color: '#ffffff',
                  padding: '10px',
                  borderRadius: '6px'
                }}
              >
                <option value="individual">Individual (1 device)</option>
                <option value="professional">Professional (3 devices)</option>
                <option value="business">Business (10 devices)</option>
                <option value="enterprise">Enterprise (unlimited)</option>
              </select>
            </div>

            <div>
              <label style={{ color: '#e2e8f0', fontSize: '14px', marginBottom: '6px', display: 'block' }}>
                Duration (days)
              </label>
              <Input
                type="number"
                value={formData.duration_days}
                onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) })}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  color: '#ffffff'
                }}
              />
            </div>

            <div>
              <label style={{ color: '#e2e8f0', fontSize: '14px', marginBottom: '6px', display: 'block' }}>
                Company Name (optional)
              </label>
              <Input
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  color: '#ffffff'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <Button
                type="submit"
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
                  color: '#ffffff'
                }}
              >
                Create License
              </Button>
              <Button
                type="button"
                onClick={onClose}
                style={{
                  flex: 1,
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff'
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLicenseManager;
